import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {graphql} from 'react-apollo';
import {autobind} from 'core-decorators';
import {Grid, Collapse} from '@material-ui/core';
import classNames from 'classnames';
import {CloseOutlined} from '@material-ui/icons';
import cartQuery from 'graphql/queries/cart.gql';
import addToCartMutation from 'graphql/mutations/carts/add-to-cart.gql';
import Button from 'components/button';
import {formatMoney, getVariationOptions, isMobile} from 'core/utils';
import {Form, Field, Input, Select, Toggle} from 'components/forms';
import styles from './styles.css';

@graphql(addToCartMutation, {
    props: ({mutate}) => ({
        onSubmit: variables => mutate({
            variables,
            refetchQueries: [{
                query: cartQuery,
            }],
        }),
    }),
})
@autobind
export default class ProductCard extends Component {
    static propTypes = {
        product: PropTypes.object.isRequired,
        isExpanded: PropTypes.bool.isRequired,
        handleOpenDetail: PropTypes.func.isRequired,
        handlOnCloseDetail: PropTypes.func.isRequired,
        handleOpenModal: PropTypes.func.isRequired,
    };

    state = {
        collapse: false,
        loading: false,
    }

    getLabelConfig(product) {
        if (product.isRestricted) return {type: 'restricted', text: 'Nidera'};
        if (product.variations.every(variation => !variation.stock)) return {type: 'stock', text: 'Sin stock'};
        if (product.isPreOrder) return {type: 'preOrder', text: 'A reservar'};
        if (product.isNew) return {type: 'new', text: 'Nuevo'};
    }

    validateStock(quantity, {variation: selectedVariation}) {
        const variation = this.props.product.variations.find(variation => variation.id === selectedVariation);
        return quantity <= variation.stock || 'No hay stock disponible.';
    }

    handleMouseOver = () => {
        this.setState({collapse: true});
    };

    handleMouseLeave = () => {
        this.setState({collapse: false});
    };

    async handleSubmit(values) {
        this.setState({loading: true});
        await this.props.onSubmit(values);
        this.setState({loading: false, showSuccess: true});
        this.timeout = setTimeout(() => this.setState({showSuccess: false}), 5000);
    }

    renderLabel(product) {
        const config = this.getLabelConfig(product);
        if (!config) return;
        return (
            <div className={styles.labelContainer}>
                <div className={classNames(styles.label, styles[config.type])}>
                    <div>{config.text}</div>
                </div>
            </div>
        );
    }

    renderPrice(product) {
        return (
            <div className={styles.priceContainer}>
                <div className={classNames(styles.price)}>
                    <div>{formatMoney(product, 'price', 2, 2, true)}</div>
                </div>
            </div>
        );
    }

    render() {
        const {product, handleOpenDetail, handlOnCloseDetail, isExpanded, handleOpenModal} = this.props;
        const shouldCollapse = isMobile() || this.state.collapse || isExpanded;

        return (
            <div onMouseOver={this.handleMouseOver} onMouseLeave={this.handleMouseLeave} className={styles.root}>
                <div className={styles.card}>
                    {!isExpanded && (
                        <div className={styles.image}>
                            <img src={product.thumbnail} alt='foto de producto' />
                            {this.renderLabel(product)}
                            {this.renderPrice(product)}
                        </div>
                    )}
                    {isExpanded && (
                        <Form
                            fields={[
                                {key: 'variation', initial: getVariationOptions(product)[0].value},
                                {key: 'quantity', initial: '1', validators: [this.validateStock]},
                                {key: 'withOrganizationLogo', initial: false, required: false},
                            ]}
                            errors={this.state.errors}
                            onSubmit={this.handleSubmit}>
                            {({getFieldProps, renderErrors, isValid, values}) => (
                                <Grid
                                    className={styles.formContainer}
                                    spacing={2}
                                    container
                                    justify='center'
                                    direction='column'>
                                    <div onClick={handlOnCloseDetail} className={styles.close}>
                                        <CloseOutlined />
                                    </div>
                                    <Grid item xs={12}>
                                        <Field label='Cantidad' vertical>
                                            <Input
                                                className={styles.input}
                                                type='number'
                                                min={1}
                                                {...getFieldProps('quantity')}
                                            />
                                            {renderErrors('quantity')}
                                        </Field>
                                    </Grid>
                                    {product.variations.length > 1 && (
                                        <Grid item xs={12}>
                                            <Field label='variación' vertical>
                                                <div>
                                                    <Select
                                                        className={styles.variation}
                                                        theme='outline'
                                                        options={getVariationOptions(product)}
                                                        {...getFieldProps('variation')}
                                                    />
                                                    {renderErrors('variation')}
                                                </div>
                                            </Field>
                                        </Grid>
                                    )}
                                    {product.allowOrganizationLogo && (
                                        <Grid item xs={12}>
                                            <Field label='Con logo' horizontal>
                                                <Toggle type='checkbox' {...getFieldProps('withOrganizationLogo')} disabled={!(values.quantity >= 15)} />
                                                {renderErrors('withOrganizationLogo')}
                                            </Field>
                                        </Grid>
                                    )}
                                    {renderErrors('__all__')}
                                    <Grid item xs={12}>
                                        {product.isPreOrder && <p className={styles.preOrder}>Fecha de entrega a confirmar</p>}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            type='submit'
                                            fullWidth
                                            theme='yellow'
                                            disabled={!isValid() || this.state.loading}>
                                            {this.state.loading ? 'AGREGANDO PRODUCTO...' : 'AGREGAR AL CARRITO'}
                                        </Button>
                                    </Grid>
                                    {this.state.showSuccess && (
                                        <div className={styles.success}>
                                            <i className={styles.check} /> Producto agregado al carrito
                                        </div>
                                    )}
                                </Grid>
                            )}
                        </Form>
                    )}
                    <Collapse in={shouldCollapse}>
                        <div className={styles.text}>
                            <p className={styles.name}>{product.name}</p>
                        </div>
                        <Grid className={styles.buttonGrid} container spacing={2} justify='center'>
                            <Grid item xs={12} sm={6}>
                                <Button onClick={() => handleOpenDetail(product.id)} fullWidth theme='yellow'>Comprar</Button>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Button onClick={() => handleOpenModal(product.id)} className={styles.button} fullWidth>Ver</Button>
                            </Grid>
                        </Grid>
                    </Collapse>
                </div>
                {!shouldCollapse && (
                    <div className={styles.text}>
                        <p className={styles.name}>{product.name}</p>
                    </div>
                )}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./components/product-card/index.js